import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@/context/ThemeContext';
import { useLanguage } from '@/context/LanguageContext';
import { useAuth } from '@/context/AuthContext';
import { useStore, selectUser } from '@/utils/store';
import { exportTransactionsCSV } from '@/utils/export';
import { useCurrency } from '@/hooks/useCurrency';
import { CURRENCIES } from '@/utils/format';
import Modal from '@/components/ui/Modal';
import Button from '@/components/ui/Button';
import PhotoPicker from '@/components/ui/PhotoPicker';
import JoinResourceModal from '@/components/shared/JoinResourceModal';
import {
  Moon,
  Sun,
  Globe,
  Coins,
  Bell,
  Shield,
  Download,
  Info,
  LogOut,
  ChevronRight,
  MessageCircle,
  Pencil,
  Briefcase,
  Building2,
  Camera,
  Users,
} from 'lucide-react';
import Badge from '@/components/ui/Badge';

function getInitials(name) {
  return name
    .split(' ')
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

export default function Settings() {
  const { t } = useTranslation();
  const { dark, toggle } = useTheme();
  const { language, setLanguage } = useLanguage();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const user = useStore(selectUser);
  const updateUser = useStore((s) => s.updateUser);
  const chatEnabled = useStore((s) => s.appSettings?.chatEnabled !== false);
  const businessEnabled = useStore((s) => s.appSettings?.businessEnabled === true);
  const propertiesEnabled = useStore((s) => s.appSettings?.propertiesEnabled === true);
  const scanEnabled = useStore((s) => s.appSettings?.scanEnabled !== false);
  const updateAppSettings = useStore((s) => s.updateAppSettings);
  const { currency, setCurrency } = useCurrency();

  const [showJoinModal, setShowJoinModal] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [profileName, setProfileName] = useState('');
  const [profileEmail, setProfileEmail] = useState('');
  const [profilePhoto, setProfilePhoto] = useState(null);

  const authUser = useAuth().user;

  const openProfileModal = () => {
    setProfileName(user.name);
    setProfileEmail(user.email);
    setProfilePhoto(user.photo || user.photoUrl || authUser?.photoUrl || null);
    setShowProfileModal(true);
  };

  const saveProfile = () => {
    const name = profileName.trim();
    if (!name) return;
    updateUser({
      name,
      email: profileEmail.trim(),
      initials: getInitials(name),
      photo: profilePhoto || undefined,
    });
    setShowProfileModal(false);
  };

  const cycleCurrency = () => {
    const idx = CURRENCIES.findIndex((c) => c.code === currency);
    const next = CURRENCIES[(idx + 1) % CURRENCIES.length];
    setCurrency(next.code);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/', { replace: true });
  };

  const sections = [
    {
      label: t('settings.sectionAppearance'),
      items: [
        {
          icon: dark ? Moon : Sun,
          label: t('settings.darkMode'),
          action: 'toggle',
          value: dark,
          onToggle: toggle,
        },
        {
          icon: Globe,
          label: t('settings.language'),
          action: 'select',
          value: language === 'es' ? 'Español' : 'English',
          onSelect: () => setLanguage(language === 'es' ? 'en' : 'es'),
        },
        {
          icon: Coins,
          label: t('settings.currency'),
          action: 'select',
          value: currency,
          onSelect: cycleCurrency,
        },
      ],
    },
    {
      label: t('settings.sectionFeatures'),
      items: [
        {
          icon: Briefcase,
          label: t('settings.businessMode'),
          action: 'toggle',
          value: businessEnabled,
          onToggle: () => updateAppSettings({ businessEnabled: !businessEnabled }),
        },
        {
          icon: Building2,
          label: t('settings.propertiesMode'),
          action: 'toggle',
          value: propertiesEnabled,
          onToggle: () => updateAppSettings({ propertiesEnabled: !propertiesEnabled }),
        },
        {
          icon: Camera,
          label: t('settings.scanButton'),
          action: 'toggle',
          value: scanEnabled,
          onToggle: () => updateAppSettings({ scanEnabled: !scanEnabled }),
        },
        {
          icon: MessageCircle,
          label: t('settings.chatAssistant'),
          action: 'toggle',
          value: chatEnabled,
          onToggle: () => updateAppSettings({ chatEnabled: !chatEnabled }),
        },
      ],
    },
    {
      label: t('settings.sectionGeneral'),
      items: [
        { icon: Users, label: t('collaborators.joinResource'), action: 'navigate', onToggle: () => setShowJoinModal(true) },
        { icon: Bell, label: t('settings.notifications'), action: 'navigate' },
        { icon: Shield, label: t('settings.security'), action: 'navigate' },
        { icon: Download, label: t('settings.export'), action: 'navigate', onToggle: () => exportTransactionsCSV(useStore.getState().transactions) },
      ],
    },
    {
      label: t('settings.sectionAccount'),
      items: [
        { icon: Info, label: t('settings.about'), action: 'navigate' },
        { icon: LogOut, label: t('settings.logout'), action: 'danger', onToggle: handleLogout },
      ],
    },
  ];

  return (
    <div className="space-y-4 animate-fade-in">
      <h1 className="text-2xl font-display font-bold text-gray-900 dark:text-white">
        {t('settings.title')}
      </h1>

      {/* Profile card */}
      <button
        onClick={openProfileModal}
        className="card flex items-center gap-4 w-full text-left hover:border-brand-300 dark:hover:border-brand-600 transition-colors group"
      >
        <div className="relative">
          {(user.photo || user.photoUrl || authUser?.photoUrl) ? (
            <img src={user.photo || user.photoUrl || authUser?.photoUrl} alt="" className="w-14 h-14 rounded-full object-cover" />
          ) : (
            <div className="w-14 h-14 rounded-full bg-gradient-to-br from-brand-500 to-accent-500 flex items-center justify-center text-white text-xl font-bold">
              {user.initials}
            </div>
          )}
          <div className="absolute -bottom-0.5 -right-0.5 w-5 h-5 rounded-full bg-brand-500 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
            <Pencil size={10} className="text-white" />
          </div>
        </div>
        <div className="flex-1">
          <div className="font-semibold text-gray-900 dark:text-white">{user.name}</div>
          <div className="text-sm text-gray-500">{user.email}</div>
          <Badge variant="income" className="mt-1">
            {t('settings.plan', { plan: user.plan })}
          </Badge>
        </div>
        <ChevronRight size={16} className="text-gray-400" />
      </button>

      {/* Settings sections */}
      {sections.map((section, si) => (
        <div key={si}>
          {section.label && (
            <div className="px-1 pb-1.5 pt-2 text-[10px] font-bold uppercase tracking-wider text-gray-400 dark:text-gray-500">
              {section.label}
            </div>
          )}
          <div className="card p-0 divide-y divide-gray-100 dark:divide-gray-800">
          {section.items.map((item, ii) => (
            <button
              key={ii}
              className="w-full flex items-center gap-3 px-5 py-4 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
              onClick={item.onToggle || item.onSelect}
            >
              <item.icon
                size={20}
                className={
                  item.action === 'danger'
                    ? 'text-danger-500'
                    : 'text-gray-500 dark:text-gray-400'
                }
              />
              <span
                className={`flex-1 text-left text-sm font-medium ${
                  item.action === 'danger'
                    ? 'text-danger-500'
                    : 'text-gray-900 dark:text-white'
                }`}
              >
                {item.label}
              </span>

              {item.action === 'toggle' && (
                <div
                  className={`w-11 h-6 rounded-full transition-colors relative ${
                    item.value ? 'bg-brand-600' : 'bg-gray-300 dark:bg-gray-600'
                  }`}
                >
                  <div
                    className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${
                      item.value ? 'translate-x-5' : 'translate-x-0.5'
                    }`}
                  />
                </div>
              )}

              {item.action === 'select' && (
                <span className="text-sm text-gray-500">{item.value}</span>
              )}

              {item.action === 'navigate' && (
                <ChevronRight size={16} className="text-gray-400" />
              )}
            </button>
          ))}
          </div>
        </div>
      ))}

      <JoinResourceModal open={showJoinModal} onClose={() => setShowJoinModal(false)} />

      {/* Profile edit modal */}
      <Modal open={showProfileModal} onClose={() => setShowProfileModal(false)} title={t('settings.editProfile')}>
        <div className="space-y-5">
          {/* Photo */}
          <div className="flex justify-center">
            <PhotoPicker
              photo={profilePhoto}
              initials={profileName ? getInitials(profileName) : user.initials}
              size={80}
              onPhotoChange={setProfilePhoto}
            />
          </div>
          {profilePhoto && (
            <button
              onClick={() => setProfilePhoto(null)}
              className="block mx-auto text-xs text-danger-500 hover:text-danger-600 transition-colors"
            >
              {t('settings.removePhoto')}
            </button>
          )}

          {/* Name */}
          <div>
            <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 block">
              {t('settings.profileName')}
            </label>
            <input
              type="text"
              className="input"
              value={profileName}
              onChange={(e) => setProfileName(e.target.value)}
            />
          </div>

          {/* Email */}
          <div>
            <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 block">
              {t('settings.profileEmail')}
            </label>
            <input
              type="email"
              className="input"
              value={profileEmail}
              onChange={(e) => setProfileEmail(e.target.value)}
            />
          </div>

          <Button className="w-full" onClick={saveProfile}>
            {t('common.save')}
          </Button>
        </div>
      </Modal>
    </div>
  );
}
